import { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle } from "@/components/Kpi";
import { ComparisonChart, PieBreakdown } from "@/components/Charts";
import { MESI } from "@/lib/format";

const inp = "border border-slate-200 rounded-md px-3 py-2 text-sm bg-white text-slate-700 " +
  "focus:outline-none focus:ring-2 focus:ring-sky-300 transition-colors duration-200";
const lbl = "block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1";
const FORMATI = [{ k: "num", l: "Numero" }, { k: "eur", l: "Euro" }, { k: "perc", l: "Percentuale" }];
const TIPI = [{ k: "bar", l: "Istogramma" }, { k: "line", l: "Linee" }, { k: "pie", l: "Torta" }];
const PALETTE = ["#0284C7", "#DC2626", "#16A34A", "#F59E0B", "#7C3AED", "#0F766E", "#94A3B8"];

export default function FormuleGrafici() {
  const { isMaster: CREAZIONE_ABILITATA } = useAuth();
  const [tab, setTab] = useState("formule");
  const [meta, setMeta] = useState({ metriche: [], formule: [], dimensioni: [] });
  const [formule, setFormule] = useState([]);
  const [grafici, setGrafici] = useState([]);
  const [nf, setNf] = useState({ nome: "", expr: "", formato: "num", filtri: {} });
  const [anteprima, setAnteprima] = useState(null);
  const [ng, setNg] = useState({ nome: "", tipo: "bar", dimensione: "mese", formato: "num", progressivo: false,
    serie: [{ nome: "", metrica: "", colore: PALETTE[0] }], filtri: {} });
  const [antGrafico, setAntGrafico] = useState(null);
  const [nd, setNd] = useState({ label: "", campo: "" });

  const ricarica = () => {
    api.get("/metriche").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/formule").then((r) => setFormule(r.data.formule || [])).catch(() => {});
    api.get("/grafici").then((r) => setGrafici(r.data.grafici || [])).catch(() => {});
  };
  useEffect(ricarica, []);

  const voci = [...meta.metriche, ...meta.formule];
  const inserisci = (key) => setNf((f) => ({ ...f, expr: `${f.expr}${f.expr ? " " : ""}${key}` }));

  const salvaFormula = async () => {
    try {
      await api.post("/formule", nf);
      toast.success("Formula salvata: ora è utilizzabile in altre formule e nei grafici");
      setNf({ nome: "", expr: "", formato: "num", filtri: {} });
      setAnteprima(null);
      ricarica();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    }
  };

  const provaFormula = async () => {
    try {
      const r = await api.post("/formule/anteprima", { expr: nf.expr, filtri: nf.filtri });
      setAnteprima(r.data.valore);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Espressione non valida");
    }
  };

  const salvaGrafico = async () => {
    try {
      await api.post("/grafici", ng);
      toast.success("Grafico salvato: puoi aggiungerlo alle dashboard con il pulsante Modifica");
      setNg({ nome: "", tipo: "bar", dimensione: "mese", formato: "num", progressivo: false,
        serie: [{ nome: "", metrica: "", colore: PALETTE[0] }], filtri: {} });
      setAntGrafico(null);
      ricarica();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    }
  };

  const provaGrafico = async () => {
    try {
      const r = await api.post("/grafici/anteprima", ng);
      setAntGrafico(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anteprima non riuscita");
    }
  };

  const tabCls = (k) => `px-4 py-2 rounded-md text-sm font-semibold transition-colors duration-200 ${
    tab === k ? "bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`;

  return (
    <div data-testid="formule-grafici-page">
      <SectionTitle sub="Crea metriche personalizzate e grafici riutilizzabili nelle dashboard.">
        Formule e grafici
      </SectionTitle>
      {!CREAZIONE_ABILITATA && (
        <div className="mb-6 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-3"
          data-testid="avviso-sola-lettura">
          Sezione in sola lettura: solo l&apos;utente Master può creare o eliminare formule e grafici.
        </div>
      )}
      <div className="flex gap-3 mb-6">
        <button type="button" className={tabCls("formule")} onClick={() => setTab("formule")} data-testid="tab-formule">Formule</button>
        <button type="button" className={tabCls("grafici")} onClick={() => setTab("grafici")} data-testid="tab-grafici">Grafici</button>
        <button type="button" className={tabCls("dimensioni")} onClick={() => setTab("dimensioni")} data-testid="tab-dimensioni">Dimensioni asse X</button>
      </div>

      {tab === "formule" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card className="p-6" data-testid="card-nuova-formula">
            <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">Nuova formula</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Nome *</label>
                <input className={`${inp} w-full`} value={nf.nome} data-testid="formula-nome"
                  onChange={(e) => setNf({ ...nf, nome: e.target.value })} placeholder="es. Resa premi su caricato" />
              </div>
              <div>
                <label className={lbl}>Formato</label>
                <select className={`${inp} w-full`} value={nf.formato} data-testid="formula-formato"
                  onChange={(e) => setNf({ ...nf, formato: e.target.value })}>
                  {FORMATI.map((f) => <option key={f.k} value={f.k}>{f.l}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className={lbl}>Espressione * (usa + − × ÷ e parentesi)</label>
              <textarea className={`${inp} w-full font-mono min-h-[70px]`} value={nf.expr} data-testid="formula-expr"
                onChange={(e) => setNf({ ...nf, expr: e.target.value })}
                placeholder="(importo_cpi + importo_cvt) / importo_caricato * 100" />
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={lbl}>Filtro AUTO / NON AUTO</label>
                <select className={`${inp} w-full`} value={nf.filtri.macro || ""} data-testid="formula-filtro-macro"
                  onChange={(e) => setNf({ ...nf, filtri: { ...nf.filtri, macro: e.target.value } })}>
                  <option value="">Nessuno</option>
                  <option value="AUTO">AUTO</option>
                  <option value="NON AUTO">NON AUTO</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Filtro mese</label>
                <select className={`${inp} w-full`} value={nf.filtri.mese || ""} data-testid="formula-filtro-mese"
                  onChange={(e) => setNf({ ...nf, filtri: { ...nf.filtri, mese: e.target.value } })}>
                  <option value="">Nessuno</option>
                  {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Filtro commerciale (slug)</label>
                <input className={`${inp} w-full`} value={nf.filtri.commerciale || ""} data-testid="formula-filtro-commerciale"
                  onChange={(e) => setNf({ ...nf, filtri: { ...nf.filtri, commerciale: e.target.value } })}
                  placeholder="es. claudio-fossi" />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button type="button" onClick={provaFormula} data-testid="formula-prova" disabled={!CREAZIONE_ABILITATA}
                className="text-sm px-4 py-2 rounded-md border border-slate-300 bg-white text-slate-700
                  hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">Prova</button>
              <button type="button" onClick={salvaFormula} data-testid="formula-salva" disabled={!CREAZIONE_ABILITATA || !nf.nome || !nf.expr}
                className="text-sm px-5 py-2 rounded-md bg-sky-600 text-white font-semibold
                  hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">Salva formula</button>
              {anteprima !== null && (
                <span className="font-mono text-sm text-slate-700" data-testid="formula-anteprima">
                  Risultato: {anteprima === null ? "n/d" : Number(anteprima).toLocaleString("it-IT", { maximumFractionDigits: 2 })}
                </span>
              )}
            </div>
            <div className="mt-6">
              <div className={lbl}>Metriche e formule disponibili (clicca per inserire)</div>
              <div className="flex flex-wrap gap-2 max-h-[220px] overflow-y-auto">
                {voci.map((m) => (
                  <button key={m.key} type="button" onClick={() => inserisci(m.key)}
                    data-testid={`metrica-${m.key}`}
                    className="text-xs px-2.5 py-1.5 rounded-full border border-slate-200 bg-slate-50
                      text-slate-600 hover:border-sky-400 hover:text-sky-700 transition-colors duration-200">
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="card-formule-salvate">
            <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">
              Formule salvate ({formule.length})
            </h3>
            {!formule.length ? <p className="text-sm text-slate-500">Nessuna formula creata.</p> : (
              <div className="space-y-3">
                {formule.map((f) => (
                  <div key={f.id} className="flex items-start gap-3 border border-slate-200 rounded-md p-3"
                    data-testid={`formula-riga-${f.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{f.nome}</div>
                      <div className="font-mono text-xs text-slate-500 break-all">{f.expr}</div>
                      <div className="text-[11px] text-slate-400">
                        chiave: {f.slug} · formato {f.formato}
                        {Object.keys(f.filtri || {}).length ? ` · filtri: ${JSON.stringify(f.filtri)}` : ""}
                      </div>
                    </div>
                    <button type="button" data-testid={`formula-elimina-${f.id}`} disabled={!CREAZIONE_ABILITATA}
                      onClick={async () => { await api.delete(`/formule/${f.id}`); ricarica(); }}
                      className="ml-auto text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "grafici" && (
        <div className="space-y-6">
          <Card className="p-6" data-testid="card-nuovo-grafico">
            <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">Nuovo grafico</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className={lbl}>Nome *</label>
                <input className={`${inp} w-full`} value={ng.nome} data-testid="grafico-nome"
                  onChange={(e) => setNg({ ...ng, nome: e.target.value })} />
              </div>
              <div>
                <label className={lbl}>Tipo</label>
                <select className={`${inp} w-full`} value={ng.tipo} data-testid="grafico-tipo"
                  onChange={(e) => setNg({ ...ng, tipo: e.target.value })}>
                  {TIPI.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Asse X (dimensione)</label>
                <select className={`${inp} w-full`} value={ng.dimensione} data-testid="grafico-dimensione"
                  onChange={(e) => setNg({ ...ng, dimensione: e.target.value })}>
                  {(meta.dimensioni || []).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Formato valori</label>
                <select className={`${inp} w-full`} value={ng.formato} data-testid="grafico-formato"
                  onChange={(e) => setNg({ ...ng, formato: e.target.value })}>
                  {FORMATI.map((f) => <option key={f.k} value={f.k}>{f.l}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" checked={!!ng.progressivo} data-testid="grafico-progressivo"
                    onChange={(e) => setNg({ ...ng, progressivo: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400" />
                  Progressivo (ogni periodo somma i precedenti)
                </label>
              </div>
            </div>

            <div className="mt-5">
              <div className={lbl}>Serie (asse Y)</div>
              {ng.serie.map((s, i) => (
                <div key={i} className="flex flex-wrap items-end gap-3 mb-3" data-testid={`serie-${i}`}>
                  <div>
                    <label className={lbl}>Metrica / formula</label>
                    <select className={`${inp} min-w-[240px]`} value={s.metrica} data-testid={`serie-metrica-${i}`}
                      onChange={(e) => {
                        const serie = [...ng.serie];
                        const v = voci.find((x) => x.key === e.target.value);
                        serie[i] = { ...s, metrica: e.target.value, nome: s.nome || (v?.label || "") };
                        setNg({ ...ng, serie });
                      }}>
                      <option value="">—</option>
                      {voci.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Etichetta</label>
                    <input className={inp} value={s.nome} data-testid={`serie-nome-${i}`}
                      onChange={(e) => {
                        const serie = [...ng.serie]; serie[i] = { ...s, nome: e.target.value };
                        setNg({ ...ng, serie });
                      }} />
                  </div>
                  <div>
                    <label className={lbl}>Colore</label>
                    <select className={inp} value={s.colore} data-testid={`serie-colore-${i}`}
                      onChange={(e) => {
                        const serie = [...ng.serie]; serie[i] = { ...s, colore: e.target.value };
                        setNg({ ...ng, serie });
                      }}>
                      {PALETTE.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  {ng.serie.length > 1 && (
                    <button type="button" data-testid={`serie-rimuovi-${i}`}
                      onClick={() => setNg({ ...ng, serie: ng.serie.filter((_, j) => j !== i) })}
                      className="text-red-500 hover:text-red-700 pb-2 transition-colors duration-200">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" data-testid="serie-aggiungi" disabled={!CREAZIONE_ABILITATA}
                onClick={() => setNg({ ...ng, serie: [...ng.serie,
                  { nome: "", metrica: "", colore: PALETTE[ng.serie.length % PALETTE.length] }] })}
                className="inline-flex items-center gap-1 text-sm text-sky-700 hover:text-sky-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
                <Plus size={14} /> Aggiungi serie
              </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={provaGrafico} data-testid="grafico-prova" disabled={!CREAZIONE_ABILITATA}
                className="text-sm px-4 py-2 rounded-md border border-slate-300 bg-white text-slate-700
                  hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">Anteprima</button>
              <button type="button" onClick={salvaGrafico}
                data-testid="grafico-salva" disabled={!CREAZIONE_ABILITATA || !ng.nome || !ng.serie.some((s) => s.metrica)}
                className="text-sm px-5 py-2 rounded-md bg-sky-600 text-white font-semibold
                  hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">Salva grafico</button>
            </div>
          </Card>

          {antGrafico && (
            <div data-testid="grafico-anteprima">
              {ng.tipo === "pie" ? (
                <PieBreakdown title={ng.nome || "Anteprima"} legendSide
                  valueKind={ng.formato === "eur" ? "eur" : "num"}
                  data={(antGrafico.dati || []).map((r) => ({ name: r.label, value: r.s0 }))} />
              ) : (
                <ComparisonChart title={ng.nome || "Anteprima"} height={320}
                  type={ng.tipo === "line" ? "line" : "bar"} data={antGrafico.dati || []}
                  keys={antGrafico.keys || []} labelFormat={ng.formato === "num" ? "int" : "compact"} />
              )}
            </div>
          )}

          <Card className="p-6" data-testid="card-grafici-salvati">
            <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">
              Grafici salvati ({grafici.length})
            </h3>
            {!grafici.length ? <p className="text-sm text-slate-500">Nessun grafico creato.</p> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {grafici.map((g) => (
                  <div key={g.id} className="flex items-start gap-3 border border-slate-200 rounded-md p-3"
                    data-testid={`grafico-riga-${g.id}`}>
                    <div>
                      <div className="font-medium text-sm">{g.nome}</div>
                      <div className="text-[11px] text-slate-500">
                        {g.tipo} · asse X: {g.dimensione}{g.progressivo ? " · progressivo" : ""} · {g.serie.map((s) => s.nome).join(", ")}
                      </div>
                    </div>
                    <button type="button" data-testid={`grafico-elimina-${g.id}`} disabled={!CREAZIONE_ABILITATA}
                      onClick={async () => { await api.delete(`/grafici/${g.id}`); ricarica(); }}
                      className="ml-auto text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "dimensioni" && (
        <Card className="p-6" data-testid="card-dimensioni">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">
            Dimensioni disponibili per l'asse X
          </h3>
          <div className="flex flex-wrap gap-2 mb-6">
            {(meta.dimensioni || []).map((d) => (
              <span key={d.key} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                {d.label} · <span className="font-mono">{d.campo}</span>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={lbl}>Nome nuova dimensione</label>
              <input className={inp} value={nd.label} data-testid="dimensione-label"
                onChange={(e) => setNd({ ...nd, label: e.target.value })} placeholder="es. Filiale" />
            </div>
            <div>
              <label className={lbl}>Campo dati</label>
              <input className={`${inp} font-mono`} value={nd.campo} data-testid="dimensione-campo"
                onChange={(e) => setNd({ ...nd, campo: e.target.value })} placeholder="es. filiale_caricamento" />
            </div>
            <button type="button" data-testid="dimensione-salva" disabled={!CREAZIONE_ABILITATA}
              onClick={async () => {
                try {
                  await api.post("/dimensioni", nd);
                  toast.success("Dimensione creata");
                  setNd({ label: "", campo: "" });
                  ricarica();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Creazione non riuscita");
                }
              }}
              className="text-sm px-5 py-2 rounded-md bg-sky-600 text-white font-semibold
                hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">Crea dimensione</button>
          </div>
        </Card>
      )}
    </div>
  );
}
